import React from "react";
import { Box, FormControl, FormLabel, Option, Sheet } from "@mui/joy";
import CommonModal from "components/common/CommonModal";
import DarkCard from "components/common/DarkCard";
import DarkSelect from "components/common/DarkSelect";
import DarkTypography from "components/common/DarkTypography";
import { useAuth } from "contexts/api";
import { useSMSTemplate } from "contexts/api/smsTemplates";
import { useAlert } from "contexts/app/notificationToast";
import { useConfirmationModal } from "contexts/app/confirmation";
import type { SMSPlaceholderDefinition, SMSTemplate } from "api/smsTemplates/SMSTemplate.typed";
import type { ZekayCustomer } from "api/customers/Customer.typed";
import type { Excursion } from "api/excursions/Excursion.typed";
import type { ExcursionModalUser } from "../excursionModalTypes";
import CuratedEmojiPicker from "components/common/CuratedEmojiPicker";
import PersonChip from "components/common/PersonChip";
import IconButtonCTA from "components/common/IconButtonCTA";
import SMSMessageChipEditor, {
  type SMSMessageChipEditorHandle,
} from "components/shared/sms/SMSMessageChipEditor";
import { SMSPlaceholderChips } from "components/shared/sms/SMSPlaceholderChips";
import SMSLengthLabel from "components/shared/sms/SMSLengthLabel";
import { getSMSDispatchTotal, smsTextHasPlaceholders } from "components/shared/sms/smsLength";
import { renderSMSPreviewBody } from "components/communication/smsEditor/smsEditorShared";
import { firstLastQualificationLabel } from "components/excursions/shared/utils/excursionUtils";
import { SArrowBack, SDelete, SHistory, SRefresh, SSend } from "styles/StyledMUI";
import { createSmsDispatchItem, useExcursionSmsDispatch } from "../hooks/useExcursionSmsDispatch";
import { useExcursionSmsHistory } from "../hooks/useExcursionSmsHistory";
import ExcursionSmsHistory from "./ExcursionSmsHistory";
import ExcursionSmsSendProgress from "./ExcursionSmsSendProgress";
import { colorTokens, mailEditorVariablesZoneSx, textTokens } from "styles/globalStyles";
import { GROUP_DEFAULT_COLOR } from "utils/colorPalette";

const SMS_COMPOSE_PANE_MIN_HEIGHT = 140;

export type SMSRecipientKind = "user" | "customer";

export type SMSRecipient = {
  kind: SMSRecipientKind;
  id: number;
  label: string;
  phone: string;
};

export type ExcursionSmsPreviewContext = {
  excursionName?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
};

type ExcursionSmsModalProps = {
  open: boolean;
  onClose: () => void;
  sendGroups: ExcursionSmsSendGroup[];
  initialRecipients: SMSRecipient[];
  previewContext?: ExcursionSmsPreviewContext;
};

export type ExcursionSmsSendGroup = {
  excursionId: number;
  label: string;
  userIds: number[];
  customerIds: number[];
  /** Client id → chip color (own color, otherwise the group color). */
  customerColors?: Record<number, string>;
};

function recipientKey(r: Pick<SMSRecipient, "kind" | "id">): string {
  return `${r.kind}-${r.id}`;
}

function recipientInSendGroup(r: SMSRecipient, group: ExcursionSmsSendGroup): boolean {
  if (r.kind === "user") return group.userIds.includes(r.id);

  return group.customerIds.includes(r.id);
}

function recipientChipColor(
  recipient: SMSRecipient,
  group: ExcursionSmsSendGroup,
): string | undefined {
  if (recipient.kind !== "customer") return undefined;

  return group.customerColors?.[recipient.id];
}

function SmsRecipientChip(props: {
  recipient: SMSRecipient;
  color?: string;
  onDelete?: () => void;
  onRestore?: () => void;
}) {
  const { recipient, color, onDelete, onRestore } = props;
  const chip = (
    <PersonChip
      size="sm"
      label={recipient.label}
      chipColor={color}
      onRemove={onRestore ? undefined : onDelete}
      sx={onRestore ? { opacity: 0.7, cursor: "pointer" } : undefined}
    />
  );

  if (!onRestore) return chip;

  return (
    <Box
      component="span"
      onClick={onRestore}
      sx={{ display: "inline-flex", maxWidth: "100%", cursor: "pointer" }}
    >
      {chip}
    </Box>
  );
}

function formatSmsPreviewDate(raw: string | undefined): string {
  const value = raw?.trim() ?? "";
  if (!value) return "";
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;

  return value;
}

function previewCatalogForRecipient(
  placeholders: SMSPlaceholderDefinition[],
  sample: SMSRecipient | undefined,
  replyPhone: string,
  companyName: string,
  companyEmail: string,
  preview: ExcursionSmsPreviewContext | undefined,
): SMSPlaceholderDefinition[] {
  const nameParts = sample?.label.split(" - ")[0]?.trim().split(/\s+/).filter(Boolean) ?? [];
  const first = nameParts[0] || "";
  const last = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
  const date = formatSmsPreviewDate(preview?.date);
  const start = preview?.startTime?.trim() || "";
  const end = preview?.endTime?.trim() || "";
  const excursionName = preview?.excursionName?.trim() || "";

  return placeholders.map((ph) => {
    if (ph.key === "FirstName" && first) return { ...ph, example: first };
    if (ph.key === "LastName" && last) return { ...ph, example: last };
    if (ph.key === "ReplyPhone" && replyPhone.trim()) {
      return { ...ph, example: replyPhone.trim() };
    }
    if (ph.key === "CompanyName" && companyName.trim()) {
      return { ...ph, example: companyName.trim() };
    }
    if (ph.key === "CompanyEmail" && companyEmail.trim()) {
      return { ...ph, example: companyEmail.trim() };
    }
    if (ph.key === "ExcursionName" && excursionName) {
      return { ...ph, example: excursionName };
    }
    if (ph.key === "Date" && date) return { ...ph, example: date };
    if (ph.key === "StartTime" && start) return { ...ph, example: start };
    if (ph.key === "EndTime" && end) return { ...ph, example: end };

    return ph;
  });
}

export default function ExcursionSmsModal(props: ExcursionSmsModalProps) {
  const { open, onClose, sendGroups, initialRecipients, previewContext } = props;
  const { user, userCompanies } = useAuth();
  const { listSMSTemplates, listSMSPlaceholders, getSMSSettings } = useSMSTemplate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();

  const currentCompany = React.useMemo(() => {
    const companyId = user?.company_id;
    if (companyId == null) return undefined;

    return userCompanies.find((c) => c.company?.id === companyId)?.company;
  }, [user?.company_id, userCompanies]);
  const companyName = currentCompany?.name?.trim() || "";
  const replyPhone = currentCompany?.phone?.trim() || "";
  const companyEmail = currentCompany?.mail_address?.trim() || "";

  const [recipients, setRecipients] = React.useState<SMSRecipient[]>([]);
  const [removed, setRemoved] = React.useState<SMSRecipient[]>([]);
  const [templates, setTemplates] = React.useState<SMSTemplate[]>([]);
  const [templateId, setTemplateId] = React.useState<number | "">("");
  const [message, setMessage] = React.useState("");
  const [placeholders, setPlaceholders] = React.useState<SMSPlaceholderDefinition[]>([]);
  const [providerReady, setProviderReady] = React.useState(false);
  const dispatch = useExcursionSmsDispatch();
  const { reset: resetDispatch } = dispatch;
  const inProgress = dispatch.items.length > 0;
  const historyExcursionId = sendGroups.length === 1 ? sendGroups[0]?.excursionId : undefined;
  const [showHistory, setShowHistory] = React.useState(false);
  const history = useExcursionSmsHistory(historyExcursionId, open && showHistory);
  const messageEditorRef = React.useRef<SMSMessageChipEditorHandle | null>(null);
  const sendIcon = React.useMemo(() => <SSend />, []);

  React.useEffect(() => {
    if (!open) return;
    setRecipients(initialRecipients);
    setRemoved([]);
    setTemplateId("");
    setMessage("");
    void (async () => {
      const [tpls, ph, settings] = await Promise.all([
        listSMSTemplates(),
        listSMSPlaceholders(),
        getSMSSettings(),
      ]);
      setTemplates((tpls ?? []).filter((t) => t.active));
      setPlaceholders(ph ?? []);
      setProviderReady(!!settings?.provider && !!settings.token_configured);
    })();
  }, [open, initialRecipients, listSMSTemplates, listSMSPlaceholders, getSMSSettings]);

  React.useEffect(() => {
    if (!open) return;
    resetDispatch();
    setShowHistory(false);
  }, [open, resetDispatch]);

  const messagePreviewText = React.useMemo(() => {
    const catalog = previewCatalogForRecipient(
      placeholders,
      recipients[0],
      replyPhone,
      companyName,
      companyEmail,
      previewContext,
    );

    return renderSMSPreviewBody(message, catalog);
  }, [companyEmail, companyName, message, placeholders, previewContext, recipients, replyPhone]);

  const handleTemplateChange = (id: number | "") => {
    setTemplateId(id);
    if (id === "") return;
    const tpl = templates.find((t) => t.id === id);
    if (tpl) setMessage(tpl.body);
  };

  const removeRecipient = (r: SMSRecipient) => {
    setRecipients((list) => list.filter((x) => recipientKey(x) !== recipientKey(r)));
    setRemoved((list) => {
      if (list.some((x) => recipientKey(x) === recipientKey(r))) return list;

      return [...list, r];
    });
  };

  const removeRecipientsBatch = (list: SMSRecipient[]) => {
    if (list.length === 0) return;
    const keys = new Set(list.map(recipientKey));
    setRecipients((prev) => prev.filter((x) => !keys.has(recipientKey(x))));
    setRemoved((prev) => {
      const next = [...prev];
      for (const r of list) {
        if (next.some((x) => recipientKey(x) === recipientKey(r))) continue;
        next.push(r);
      }

      return next;
    });
  };

  const restoreRecipient = (r: SMSRecipient) => {
    setRemoved((list) => list.filter((x) => recipientKey(x) !== recipientKey(r)));
    setRecipients((list) => {
      if (list.some((x) => recipientKey(x) === recipientKey(r))) return list;

      return [...list, r];
    });
  };

  const dispatchCount = React.useMemo(() => {
    let count = 0;
    for (const group of sendGroups) {
      count += recipients.filter((r) => recipientInSendGroup(r, group)).length;
    }

    return count;
  }, [recipients, sendGroups]);

  const buildPayloadBase = (): {
    template_id?: number;
    message?: string;
  } => {
    if (typeof templateId === "number") {
      const tpl = templates.find((t) => t.id === templateId);
      if (tpl && message.trim() === tpl.body.trim()) {
        return { template_id: templateId };
      }
    }

    return { message: message.trim() };
  };

  const handleSend = async () => {
    if (!providerReady) {
      showAlert("Provider SMS non configuré", "warning");

      return;
    }

    if (recipients.length === 0) {
      showAlert("Aucun destinataire", "warning");

      return;
    }

    if (!message.trim()) {
      showAlert("Message requis", "warning");

      return;
    }

    if (sendGroups.length === 0) {
      showAlert("Aucune sortie cible", "warning");

      return;
    }

    const totalSms = getSMSDispatchTotal(message, dispatchCount);
    const approx = smsTextHasPlaceholders(message);
    const ok = await showConfirmation(
      "Envoyer le SMS",
      `Envoyer à ${recipients.length} destinataire${recipients.length > 1 ? "s" : ""}` +
        (totalSms > 0 ? ` · ${approx ? "~" : ""}${totalSms} SMS au total` : "") +
        " ?",
    );
    if (!ok) return;

    const items = sendGroups.flatMap((group) =>
      recipients
        .filter((r) => recipientInSendGroup(r, group))
        .map((r) => createSmsDispatchItem(group, r)),
    );
    if (items.length === 0) {
      showAlert("Aucun destinataire sur les sorties ciblées", "warning");

      return;
    }

    await dispatch.start(items, buildPayloadBase());
  };

  const handleRetryFailed = async () => {
    const count = dispatch.counters.retryable;
    const ok = await showConfirmation("Renvoyer les échecs", `Renvoyer ${count} SMS en échec ?`);
    if (ok) await dispatch.retryFailed();
  };

  const openHistory = () => {
    dispatch.reset();
    setShowHistory(true);
  };

  const historyButton =
    historyExcursionId != null ? (
      <IconButtonCTA aria-label="SMS envoyés" ctaVariant="accent" onClick={openHistory}>
        <SHistory />
      </IconButtonCTA>
    ) : null;

  const footerActions = (() => {
    if (showHistory) {
      return (
        <>
          <IconButtonCTA
            aria-label="Retour à la saisie"
            ctaVariant="accent"
            onClick={() => setShowHistory(false)}
          >
            <SArrowBack />
          </IconButtonCTA>
          <IconButtonCTA
            aria-label="Actualiser"
            ctaVariant="accent"
            onClick={() => void history.refresh()}
            disabled={history.loading}
          >
            <SRefresh />
          </IconButtonCTA>
        </>
      );
    }

    if (!inProgress) return historyButton ?? undefined;
    if (dispatch.running) return undefined;

    return (
      <>
        {dispatch.counters.retryable > 0 ? (
          <IconButtonCTA
            aria-label="Renvoyer les échecs"
            ctaVariant="accent"
            onClick={() => void handleRetryFailed()}
          >
            <SRefresh />
          </IconButtonCTA>
        ) : null}
        {historyButton}
      </>
    );
  })();

  const modalTitle = showHistory ? "SMS envoyés" : inProgress ? "Envoi des SMS" : "Envoyer un SMS";
  const modalSubtitle = showHistory
    ? history.total > 0
      ? `${history.total} SMS`
      : undefined
    : inProgress
      ? `${dispatch.counters.done}/${dispatch.counters.total}`
      : undefined;

  const handleClose = () => {
    if (dispatch.running) return;
    dispatch.reset();
    onClose();
  };

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      closeDisabled={dispatch.running}
      title={modalTitle}
      subtitle={modalSubtitle}
      confirmLabel="Envoyer"
      confirmIcon={sendIcon}
      onConfirm={inProgress || showHistory ? undefined : () => void handleSend()}
      submitDisabled={!providerReady || recipients.length === 0 || !message.trim()}
      footerActions={footerActions}
      size="lg"
      dialogSx={{
        width: { xs: "100vw", md: "min(1120px, 94vw)" },
        maxWidth: { xs: "100vw", md: "min(1120px, 94vw)" },
        height: { xs: "100dvh", md: "96dvh" },
        maxHeight: { xs: "100dvh", md: "96dvh" },
        my: { xs: 0, md: 1 },
        mx: { xs: 0, md: "auto" },
        borderRadius: { xs: 0, md: "12px" },
        overflow: "hidden",
        "& > .MuiBox-root:nth-of-type(2)": {
          overflow: "hidden",
          pr: { xs: 0.25, sm: 0.5 },
        },
      }}
    >
      {showHistory ? (
        <ExcursionSmsHistory messages={history.messages} loading={history.loading} />
      ) : inProgress ? (
        <ExcursionSmsSendProgress
          items={dispatch.items}
          running={dispatch.running}
          globalError={dispatch.globalError}
          counters={dispatch.counters}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 1.25, md: 1.5 },
            minWidth: 0,
            width: "100%",
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
            overscrollBehavior: "contain",
          }}
        >
          <Box
            sx={{
              flex: { xs: "0 0 auto", md: "0 0 min(44%, 400px)" },
              width: { xs: "100%", md: "min(44%, 400px)" },
              maxHeight: { xs: "32dvh", md: "none" },
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 0.75, flexShrink: 0 }}>
              Destinataires · {recipients.length}
            </DarkTypography>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                overflow: "auto",
                overscrollBehavior: "contain",
                pr: 0.25,
              }}
            >
              {sendGroups.map((group) => {
                const groupRecipients = recipients.filter((r) => recipientInSendGroup(r, group));
                const groupRemoved = removed.filter((r) => recipientInSendGroup(r, group));

                return (
                  <DarkCard
                    key={group.excursionId}
                    dense
                    sx={{ minWidth: 0, p: { xs: 1, sm: 1.25 }, boxShadow: "none", flexShrink: 0 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.75,
                        minWidth: 0,
                      }}
                    >
                      <DarkTypography
                        level="body-xs"
                        colorVariant="title"
                        sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {group.label} · {groupRecipients.length}
                      </DarkTypography>
                      {groupRecipients.length > 0 ? (
                        <IconButtonCTA
                          aria-label="Tout retirer"
                          size="sm"
                          ctaVariant="danger"
                          onClick={() => removeRecipientsBatch(groupRecipients)}
                        >
                          <SDelete />
                        </IconButtonCTA>
                      ) : null}
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {groupRecipients.map((r) => (
                        <SmsRecipientChip
                          key={`${group.excursionId}-${recipientKey(r)}`}
                          recipient={r}
                          color={recipientChipColor(r, group)}
                          onDelete={() => removeRecipient(r)}
                        />
                      ))}
                      {groupRecipients.length === 0 && groupRemoved.length === 0 ? (
                        <DarkTypography level="body-xs" colorVariant="legend">
                          Aucun destinataire
                        </DarkTypography>
                      ) : null}
                    </Box>
                    {groupRemoved.length > 0 ? (
                      <Box sx={{ mt: 0.75 }}>
                        <DarkTypography level="body-xs" colorVariant="muted" sx={{ mb: 0.5 }}>
                          Retirés — cliquer pour réintégrer
                        </DarkTypography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {groupRemoved.map((r) => (
                            <SmsRecipientChip
                              key={`rm-${group.excursionId}-${recipientKey(r)}`}
                              recipient={r}
                              color={recipientChipColor(r, group)}
                              onRestore={() => restoreRecipient(r)}
                            />
                          ))}
                        </Box>
                      </Box>
                    ) : null}
                  </DarkCard>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              overflow: "hidden",
            }}
          >
            <FormControl sx={{ flexShrink: 0 }}>
              <FormLabel sx={{ color: textTokens.color.secondary }}>Modèle</FormLabel>
              <DarkSelect
                value={templateId === "" ? "" : String(templateId)}
                onChange={(_, v) => {
                  const raw = String(v ?? "");
                  handleTemplateChange(raw === "" ? "" : Number(raw));
                }}
              >
                <Option value="">Message libre</Option>
                {templates.map((t) => (
                  <Option key={t.id} value={String(t.id)}>
                    {t.name}
                  </Option>
                ))}
              </DarkSelect>
            </FormControl>

            {placeholders.length > 0 && (
              <Box sx={{ ...mailEditorVariablesZoneSx, flexShrink: 0 }}>
                <DarkTypography level="body-xs" colorVariant="muted" sx={{ mb: 0.35 }}>
                  Variables
                </DarkTypography>
                <SMSPlaceholderChips
                  placeholders={placeholders}
                  canEdit
                  onInsert={(key) => messageEditorRef.current?.insertPlaceholder(key)}
                />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                alignItems: "stretch",
                minWidth: 0,
                width: "100%",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <FormControl
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <FormLabel
                  sx={{
                    color: textTokens.color.secondary,
                    mb: 0.5,
                    flexShrink: 0,
                    minHeight: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Message
                </FormLabel>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: SMS_COMPOSE_PANE_MIN_HEIGHT,
                    minWidth: 0,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <SMSMessageChipEditor
                    ref={messageEditorRef}
                    value={message}
                    placeholders={placeholders}
                    onChange={setMessage}
                    minRows={4}
                    maxHeight="100%"
                    placeholder="Message SMS…"
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mt: 0.5,
                    flexShrink: 0,
                    minHeight: 36,
                  }}
                >
                  <CuratedEmojiPicker
                    onPick={(emoji) => messageEditorRef.current?.insertText(emoji)}
                    menuZIndex={2500}
                  />
                  <SMSLengthLabel text={message} dispatchCount={dispatchCount} />
                </Box>
              </FormControl>

              <Box
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <DarkTypography
                  level="body-sm"
                  colorVariant="muted"
                  sx={{
                    mb: 0.5,
                    flexShrink: 0,
                    minHeight: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Aperçu
                </DarkTypography>
                <Sheet
                  variant="outlined"
                  sx={{
                    p: 0,
                    flex: 1,
                    minHeight: SMS_COMPOSE_PANE_MIN_HEIGHT,
                    minWidth: 0,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    borderColor: colorTokens.border.default,
                    bgcolor: colorTokens.bg.surfaceElevated,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      minWidth: 0,
                      overflow: "auto",
                      overflowX: "hidden",
                      overscrollBehavior: "contain",
                      bgcolor: colorTokens.bg.sheetLight,
                      p: { xs: 1.25, sm: 1.5 },
                    }}
                  >
                    <DarkTypography
                      level="body-sm"
                      colorVariant="onLight"
                      sx={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        maxWidth: "100%",
                      }}
                    >
                      {messagePreviewText.trim() ? messagePreviewText : " "}
                    </DarkTypography>
                  </Box>
                </Sheet>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </CommonModal>
  );
}

type SmsColorGroup = {
  color?: string | null;
  customers?: ReadonlyArray<{ id: number; color?: string | null }> | null;
};

/** One color per client: own color, otherwise the group color. */
export function smsCustomerColors(groups: readonly SmsColorGroup[]): Record<number, string> {
  const colors: Record<number, string> = {};

  for (const group of groups) {
    const groupColor = group.color?.trim() || GROUP_DEFAULT_COLOR;

    for (const customer of group.customers ?? []) {
      if (!customer.id || customer.id <= 0) continue;

      const own = customer.color?.trim();
      if (own) {
        colors[customer.id] = own;
        continue;
      }

      if (colors[customer.id] == null) colors[customer.id] = groupColor;
    }
  }

  return colors;
}

export function collectExcursionSmsParticipantIds(args: {
  groups: Excursion["groups"] | undefined;
  params?: Record<string, unknown> | null;
}): { userIds: number[]; customerIds: number[] } {
  const userIds: number[] = [];
  const customerIds: number[] = [];

  for (const g of args.groups ?? []) {
    for (const u of g.excursion_users ?? []) {
      if (u.id > 0) userIds.push(u.id);
    }
    for (const c of g.excursion_customers ?? []) {
      if (c.id > 0) customerIds.push(c.id);
    }
  }

  const collectPicklistIds = (value: unknown, into: number[]) => {
    if (!Array.isArray(value)) return;
    for (const item of value) {
      if (item && typeof item === "object" && "id" in item) {
        const id = Number((item as { id: unknown }).id);
        if (Number.isFinite(id) && id > 0) into.push(id);
      }
    }
  };

  for (const [key, value] of Object.entries(args.params ?? {})) {
    const k = key.toLowerCase();
    if (k.includes("customer") || k.includes("client")) collectPicklistIds(value, customerIds);
    else collectPicklistIds(value, userIds);
  }

  return { userIds, customerIds };
}

export function buildExcursionSmsRecipients(args: {
  userIds: number[];
  customerIds: number[];
  users: ExcursionModalUser[];
  customers: ZekayCustomer[];
}): SMSRecipient[] {
  const usersById = new Map(args.users.map((u) => [u.id, u]));
  const customersById = new Map(args.customers.map((c) => [c.Id, c]));
  const out: SMSRecipient[] = [];
  const seen = new Set<string>();

  for (const id of args.userIds) {
    const u = usersById.get(id);
    const phone = u?.phone_number?.trim() || "";
    if (!phone) continue;
    const key = `user-${id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const label =
      firstLastQualificationLabel(u?.first_name, u?.last_name, u?.qualification) ||
      u?.name ||
      `Utilisateur #${id}`;
    out.push({
      kind: "user",
      id,
      label,
      phone,
    });
  }

  for (const id of args.customerIds) {
    const c = customersById.get(id);
    const phone = c?.Phonenumber?.trim() || "";
    if (!phone) continue;
    const key = `customer-${id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const label =
      firstLastQualificationLabel(c?.FirstName, c?.LastName, c?.LastQualification) ||
      `Client #${id}`;
    out.push({
      kind: "customer",
      id,
      label,
      phone,
    });
  }

  return out;
}
